import { useEffect, useRef, useState } from 'react'
import { Dialog } from './Dialog'
import { Card } from './Card'
import { ConeTecMark, MailIcon, PencilIcon, PhoneIcon, PlaceIcon, ShareIcon } from './icons'
import { useHubLang } from '../lib/i18n'
import { CARD, type CardCopy } from '../lib/hubCopy'
import {
  buildQrVCard,
  buildVCard,
  loadSignatureCard,
  placeLines,
  refusalFor,
  type CardRefusal,
  type SignatureCard,
} from '../lib/signature'
import { CARD_SHOWS_LOGO, telHref, useBusinessCard, vcfFileName } from '../lib/businessCard'
import { jpegBase64 } from '../lib/profilePhoto'
import { PhotoCropDialog } from './PhotoCropDialog'
import { useMyPhotoFlow } from './useMyPhotoFlow'
import { CardEditDialog } from './CardEditDialog'
import { cardImageFileName, drawCardImage } from '../lib/cardImage'
import { initialsOf } from '../lib/initials'

/**
 * The digital business card, as a modal. David, 4 Oct: "really great looking",
 * opened from the hub or the profile menu, with the person's photo (initials
 * until photos exist), the branding, the details and the QR.
 *
 * ── THREE PIECES, AND WHY THE FACE IS ITS OWN COMPONENT ─────────────────────
 *
 * `BusinessCardFace` is pure: a card and a QR data URL in, markup out. Effects
 * do not run under renderToString, so anything that fetched or generated
 * inside the face would be unreachable by the web suite with every assertion
 * green against a loading line. The dialog fetches and generates; the face
 * draws; the suite renders the face with a real card shape.
 *
 * ── THE QR IS STILL THE vCARD ITSELF (083, B21) ─────────────────────────────
 *
 * Nothing about the modal changes 083's ruling: the code encodes the contact
 * directly, generated in the browser, so there is no card page, no URL and
 * nothing for anon to read. A client on a site with no signal still gets a
 * contact. The mark in the middle of the code is affordable because the code
 * is generated at error-correction level Q (25% recoverable; M, 15%, for a long card — 134) and the badge
 * covers well under 5% of it — and the white tile around the code is its quiet
 * zone, not decoration: a QR on a tinted surface fails on cheaper cameras.
 *
 * ── THE PHOTO IS THE PERSON'S OWN (132, David 6 Oct) ────────────────────────
 *
 * Set inside this dialog, beside the phone and for the same reasons: it is
 * shown to strangers, so it is the person's to choose, and BambooHR neither
 * supplies nor overwrites it. Self-read only — see lib/profilePhoto.ts before
 * showing it anywhere else. It goes on the face and into the downloaded and
 * shared .vcf, but NEVER into the QR: a photo is ~60 KB and a code holds <3 KB,
 * so `vcard` (the QR's text) is built without it and only the file gets it.
 */

/** Public path, served by Netlify from web/public — the design system's
 *  "white on brand blue" lockup, which its versions card sanctions. */
export const CARD_LOGO_SRC = '/brand/card/conetec-logo-white.svg'

export function BusinessCardFace({
  card,
  qr,
  photoUrl = null,
  t,
}: {
  card: SignatureCard
  /** A data URL, or null while it is being drawn. */
  qr: string | null
  photoUrl?: string | null
  t: CardCopy
}) {
  const place = placeLines(card)
  return (
    <article className="bizcard" aria-label={t.cardLabel(card.displayName)}>
      <div className="bizcard__band">
        {/* The symbol's strata, large and faint, as the band's texture. It is
            the logo's own artwork, so the band is branded without a second
            logo competing with the lockup. */}
        {/* CARD_SHOWS_LOGO is false since 11 Oct (David): no logo artwork on
            the card for now — see lib/businessCard.ts. */}
        {CARD_SHOWS_LOGO && (
          <>
            <span className="bizcard__watermark" aria-hidden="true">
              <ConeTecMark size={240} />
            </span>
            <img className="bizcard__logo" src={CARD_LOGO_SRC} alt="Conetec" width={136} height={40} />
          </>
        )}
      </div>

      <div className="bizcard__who">
        <span className="bizcard__photo" aria-hidden="true">
          {photoUrl ? <img src={photoUrl} alt="" /> : initialsOf(card.displayName)}
        </span>
        <p className="bizcard__name">{card.displayName}</p>
        {card.jobTitle && <p className="bizcard__role">{card.jobTitle}</p>}
      </div>

      {/* data-kind: cardImage.ts reads each row's icon by it, so the picture
          and the face cannot disagree about which glyph is which. */}
      <ul className="bizcard__contact">
        {/* 178 (David, 12 Oct): everybody has a card. The company address
            when there is one; never a personal one (the server returns none). */}
        {card.workEmail && (
          <li data-kind="email">
            <span className="bizcard__icon" aria-hidden="true"><MailIcon size={16} /></span>
            <span className="visually-hidden">{t.emailLabel}: </span>
            <a href={`mailto:${card.workEmail}`}>{card.workEmail}</a>
          </li>
        )}
        {card.workPhone && (
          <li data-kind="mobile">
            <span className="bizcard__icon" aria-hidden="true"><PhoneIcon size={16} /></span>
            <a href={`tel:${telHref(card.workPhone)}`}>{card.workPhone}</a>
            {/* Visible, not visually-hidden: with two numbers on a card the
                reader has to know which one rings a desk. */}
            <span className="bizcard__kind">{t.mobileLabel}</span>
          </li>
        )}
        {card.officePhone && (
          <li data-kind="office">
            <span className="bizcard__icon" aria-hidden="true"><PhoneIcon size={16} /></span>
            <a href={`tel:${telHref(card.officePhone)}`}>{card.officePhone}</a>
            <span className="bizcard__kind">{t.officeLabel}</span>
          </li>
        )}
        {place.length > 0 && (
          <li data-kind="place">
            <span className="bizcard__icon" aria-hidden="true"><PlaceIcon size={16} /></span>
            <span className="visually-hidden">{t.placeLabel}: </span>
            <span className="bizcard__place">
              {place.map((line, i) => (
                <span key={i}>{line}</span>
              ))}
            </span>
          </li>
        )}
      </ul>

      <div className="bizcard__code">
        <div className="bizcard__qr">
          {qr ? (
            <>
              {/* alt="" — the code carries nothing a screen reader can use;
                  the same details are in text directly above it. */}
              <img src={qr} alt="" width={156} height={156} />
              {CARD_SHOWS_LOGO && (
                <span className="bizcard__qrmark" aria-hidden="true">
                  <ConeTecMark size={18} />
                </span>
              )}
            </>
          ) : (
            <span className="bizcard__qrwait">{t.preparingCode}</span>
          )}
        </div>
        <p className="bizcard__scan">{t.scan}</p>
      </div>

      {/* CONETEC FOR EVERYBODY (David, 6 Oct): the card names the company a
          client knows, never the registered entity — not Lankelma, not
          CONETEC PERU SAC. 4 Oct's card printed card.legalEntity here; do not
          put it back on the strength of the signature's reasoning, which is
          about email and is held anyway. */}
      <p className="bizcard__entity">Conetec</p>
    </article>
  )
}

type Load =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; card: SignatureCard }
  | { kind: 'refused'; why: CardRefusal }

/**
 * The dialog. Rendered ONCE, by AppShell, so the hub, the identity menu and
 * #/profile all open the same one — the drawer's reasoning (HLP-12).
 *
 * Fetched on FIRST OPEN, not on mount: AppShell is on every screen and most
 * screens never open the card, so a mount-time `my_signature_card()` would be
 * ~900 people times every navigation for nothing. A refusal for a missing or
 * personal address is kept (it will not change by reopening); an unexpected
 * failure is retried on the next open.
 */
export function BusinessCardDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = CARD[useHubLang()]
  const [load, setLoad] = useState<Load>({ kind: 'idle' })
  const [qr, setQr] = useState<string | null>(null)
  const [canShare, setCanShare] = useState(false)
  // 6 Oct: the card as a picture for the camera roll. Drawn AHEAD of the
  // press, because iOS only opens the share sheet inside the tap that asked
  // for it — a picture still being drawn when the tap ends is a share that
  // silently never appears.
  const [cardImage, setCardImage] = useState<File | null>(null)
  const [imageError, setImageError] = useState(false)
  const sheet = useRef<HTMLDivElement>(null)
  // 134: one Edit card button opens a dialog holding everything a person may
  // change — photo, mobile, office phone, office address.
  const [editOpen, setEditOpen] = useState(false)
  // Not a per-effect `live` flag: closing the dialog mid-fetch must not strand
  // it at 'loading', which would make every later open show a spinner forever.
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  useEffect(() => {
    if (!open) return
    if (load.kind === 'loading') return
    // 11 Oct: #/profile edits the same details, so an open after the first is
    // a SILENT refresh — the card already drawn stays up while it runs.
    if (load.kind === 'ready') {
      loadSignatureCard()
        .then((card) => mounted.current && setLoad({ kind: 'ready', card }))
        .catch(() => undefined)
      return
    }
    if (load.kind === 'refused' && load.why !== 'unknown') return
    setLoad({ kind: 'loading' })
    loadSignatureCard()
      .then((card) => mounted.current && setLoad({ kind: 'ready', card }))
      .catch((e: Error) => mounted.current && setLoad({ kind: 'refused', why: refusalFor(e?.message ?? '') }))
    // `load` is read, deliberately not a dependency: this runs on the open
    // transition and nowhere else.
  }, [open])

  const card = load.kind === 'ready' ? load.card : null
  const vcard = card ? buildQrVCard(card) : null
  // 132, lifted into useMyPhotoFlow on 11 Oct so #/profile shares it. The
  // photo is fetched once the card is there — a refusal has no face to show.
  const {
    photo,
    photoUrl,
    photoBusy,
    photoError,
    inputRef: photoInput,
    onPickPhoto,
    cropSource,
    cropBusy,
    cropError,
    closeCrop,
    onUseCrop,
    onRemovePhoto,
    clearError: clearPhotoError,
  } = useMyPhotoFlow(card !== null, t)

  useEffect(() => {
    if (!vcard) {
      setQr(null)
      return
    }
    let live = true
    // `(m.default ?? m)`: qrcode ships CommonJS, and which of the two a dynamic
    // import hands back depends on the bundler (Profile.tsx learned this).
    import('qrcode')
      .then((m) => ((m as unknown as { default?: typeof m }).default ?? m))
      .then((qrcode) =>
        qrcode.toDataURL(vcard, {
          // The white tile in hub.css is the quiet zone, so none is drawn here.
          margin: 0,
          width: 552,
          // Q where it stays small, M where Q would make the modules too fine
          // to scan off a laptop screen (134: a full card with an address was
          // version 16 at Q). The badge covers ~3% of the code; M recovers 15%.
          errorCorrectionLevel: qrLevel(qrcode, vcard),
          // A canvas has no tokens: --blue-900 on white, written out. Dark
          // brand blue keeps the contrast a camera needs.
          color: { dark: '#002b57', light: '#ffffff' },
        }),
      )
      .then((url: string) => live && setQr(url))
      .catch(() => live && setQr(null))
    return () => {
      live = false
    }
  }, [vcard])

  // Web Share with a FILE is a phone thing, and asking is the only honest test.
  // No button at all where it cannot work — an inert Share is SHL-8's trap.
  useEffect(() => {
    if (!card || typeof navigator === 'undefined' || typeof navigator.canShare !== 'function') {
      setCanShare(false)
      return
    }
    try {
      setCanShare(navigator.canShare({ files: [vcfFile(card, photoUrl)] }))
    } catch {
      setCanShare(false)
    }
  }, [card, photoUrl])

  // Redrawn whenever what it shows changes — the photo arriving, a new phone.
  // A short delay so the face is in the DOM (its SVGs are the artwork).
  useEffect(() => {
    if (!card || !qr) {
      setCardImage(null)
      return
    }
    let live = true
    const timer = window.setTimeout(() => {
      makeCardImage(card, qr)
        .then((f) => live && setCardImage(f))
        .catch(() => live && setCardImage(null))
    }, 120)
    return () => {
      live = false
      window.clearTimeout(timer)
    }
    // `photoUrl` and `t` are read inside makeCardImage; they are the inputs.
  }, [card, qr, photoUrl, t])

  async function makeCardImage(c: SignatureCard, code: string): Promise<File> {
    const blob = await drawCardImage({
      card: c,
      qr: code,
      photo: photoUrl,
      face: sheet.current?.querySelector<HTMLElement>('.bizcard') ?? null,
      logoSrc: CARD_SHOWS_LOGO ? CARD_LOGO_SRC : null,
      scanLabel: t.scan,
      labels: { mobile: t.mobileLabel, office: t.officeLabel },
    })
    return new File([blob], cardImageFileName(c), { type: 'image/png' })
  }

  /**
   * On a PHONE, the share sheet — its "Save Image" is what puts the picture in
   * Photos; a download lands in Files, where nobody looks. On a computer, a
   * plain download: Windows' share panel is not where anyone keeps a picture.
   * A cancelled sheet is not an error; a refused one falls back to download.
   */
  async function onSaveImage() {
    if (!card || !qr) return
    setImageError(false)
    let file = cardImage
    try {
      if (!file) file = await makeCardImage(card, qr)
    } catch {
      setImageError(true)
      return
    }
    const phone = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches
    if (phone && typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file] })
        return
      } catch (e) {
        if ((e as Error)?.name === 'AbortError') return
      }
    }
    const url = URL.createObjectURL(file)
    const a = document.createElement('a')
    a.href = url
    a.download = file.name
    a.click()
    window.setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  function onDownload() {
    if (!card) return
    const url = URL.createObjectURL(new Blob([buildVCard(card, jpegBase64(photoUrl))], { type: 'text/vcard;charset=utf-8' }))
    const a = document.createElement('a')
    a.href = url
    a.download = vcfFileName(card)
    a.click()
    URL.revokeObjectURL(url)
  }

  async function onShare() {
    if (!card) return
    try {
      await navigator.share({ files: [vcfFile(card, photoUrl)], title: card.displayName })
    } catch {
      // Cancelling the share sheet rejects. That is not an error to show.
    }
  }

  async function reloadCard() {
    const fresh = await loadSignatureCard()
    if (mounted.current) setLoad({ kind: 'ready', card: fresh })
  }

  return (
    <>
    <Dialog
      open={open}
      title={t.dialogTitle}
      onClose={() => {
        setEditOpen(false)
        clearPhotoError()
        closeCrop()
        onClose()
      }}
      className="cardpanel"
    >
      {card ? (
        <div className="cardsheet" ref={sheet}>
          <BusinessCardFace card={card} qr={qr} photoUrl={photoUrl} t={t} />

          <div className="cardsheet__actions">
            <button type="button" className="btn btn--primary" onClick={onDownload}>
              {t.saveContact}
            </button>
            {canShare && (
              <button type="button" className="btn btn--quiet" onClick={onShare}>
                <ShareIcon size={16} />
                {t.share}
              </button>
            )}
            {/* Disabled only until the code is drawn — the picture needs it. */}
            <button type="button" className="btn btn--quiet" onClick={() => void onSaveImage()} disabled={!qr}>
              {t.saveImage}
            </button>
          </div>
          {imageError && <p className="profile__error" role="alert">{t.imageFailed}</p>}

          <button type="button" className="btn btn--link cardsheet__edit" onClick={() => setEditOpen(true)} aria-haspopup="dialog">
            <PencilIcon size={16} />
            {t.editCard}
          </button>

          <p className="cardsheet__note">{t.howItWorks}</p>
        </div>
      ) : load.kind === 'refused' ? (
        <CardRefusalNote kind={load.why} t={t} />
      ) : (
        <div className="cardsheet__wait" role="status">
          <span className="cardsheet__spinner" aria-hidden="true" />
          {t.loading}
        </div>
      )}
    </Dialog>
    {/* SIBLINGS, not children — see PhotoCropDialog's header: inside the card
        Dialog, their Tab would bubble into the card's focus trap. The file
        input lives out here too, so it outlives whichever dialog asked. */}
    {open && (
      <input
        ref={photoInput}
        hidden
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic"
        onChange={(e) => void onPickPhoto(e.target.files?.[0])}
      />
    )}
    <CardEditDialog
      open={open && editOpen}
      card={card}
      photoUrl={photoUrl}
      photoReady={photo !== null}
      photoBusy={photoBusy || cropSource !== null}
      photoError={photoError}
      onChangePhoto={() => photoInput.current?.click()}
      onRemovePhoto={() => void onRemovePhoto()}
      onSaved={reloadCard}
      onClose={() => setEditOpen(false)}
      t={t}
    />
    <PhotoCropDialog
      source={open ? cropSource : null}
      busy={cropBusy}
      error={cropError}
      onCancel={closeCrop}
      onUse={(c) => void onUseCrop(c)}
      t={t}
    />
    </>
  )
}

/** Q unless Q pushes the code past version 12 (65 modules); then M. */
export function qrLevel(
  qrcode: { create: (text: string, opts: { errorCorrectionLevel: 'Q' | 'M' }) => { version: number } },
  text: string,
): 'Q' | 'M' {
  return qrcode.create(text, { errorCorrectionLevel: 'Q' }).version <= 12 ? 'Q' : 'M'
}

/** The FILE carries the photo; the QR's `vcard` never does (132). */
function vcfFile(card: SignatureCard, photoUrl: string | null = null): File {
  return new File([buildVCard(card, jpegBase64(photoUrl))], vcfFileName(card), { type: 'text/vcard' })
}

/**
 * ACC-24's two populations, plus the unexpected. Neither names a domain —
 * telling somebody which addresses would have worked is an oracle over the
 * allow-list and buys them nothing (Profile.tsx's Refusal, same rule).
 */
export function CardRefusalNote({ kind, t }: { kind: CardRefusal; t: CardCopy }) {
  const [head, body] =
    kind === 'no-address'
      ? [t.noAddressHead, t.noAddressBody]
      : kind === 'personal-address'
        ? [t.personalAddressHead, t.personalAddressBody]
        : [t.failedHead, t.failedBody]
  return (
    <div className="cardsheet__refusal" role="status">
      <p className="cardsheet__refusalhead">{head}</p>
      <p>{body}</p>
    </div>
  )
}

/**
 * The hub's way in: a small card in the rail that looks like the thing it
 * opens. Renders NOTHING outside the shell or while the card is held — a card
 * whose button opens nothing is SHL-8's trap.
 */
export function MyCardWidget({ name }: { name: string }) {
  const t = CARD[useHubLang()]
  const openCard = useBusinessCard()
  if (!openCard) return null
  return (
    <Card title={t.widgetTitle}>
      <button type="button" className="cardteaser" onClick={openCard} aria-haspopup="dialog" title={t.widgetLede}>
        <span className="cardteaser__band" aria-hidden="true">
          {CARD_SHOWS_LOGO && <span className="cardteaser__mark"><ConeTecMark size={72} /></span>}
        </span>
        <span className="cardteaser__photo" aria-hidden="true">{initialsOf(name)}</span>
        <span className="cardteaser__name">{name}</span>
        <span className="cardteaser__cta">{t.show}</span>
      </button>
      {/* The lede ("Meeting someone? Show your card…") is the button's
          title since 11 Oct — the rail is hooks, and the teaser says it. */}
    </Card>
  )
}

/** #/profile's way in. Same rule: nothing when there is nothing to open. */
export function OpenCardButton() {
  const t = CARD[useHubLang()]
  const openCard = useBusinessCard()
  if (!openCard) return null
  return (
    <button type="button" className="btn btn--primary" onClick={openCard} aria-haspopup="dialog">
      {t.show}
    </button>
  )
}
